import { AxiosError } from 'axios';
import { createAxiosInstance } from './axios-api-retry';
import store from '../redux/store'
import ContactCenter from '../Apps/ContactCenter/Modules/ContactCenter';
import { changeVpnStatus } from '../redux/actions/preferencesActions'
import { exponentialBackoff, sleep } from '../utils/utils';
import config from '../config/env_variables';

declare const window: any;

const VPN_CONFIG = {
  ERROR_STATUS_CODE: 550,
  DEFAULT_API_TIMEOUT: config.VPN_SET_TIMEOUT_VALUE || 3000,
  RETRY_DELAY: 1000, // lessthan 1000 is good for exponential backoff - // 2^0 = 1, 2^1 = 2, 2^2 = 4,
  MAX_RETRIES: 3,
  VPN_CHECK_INTERVAL_TIME: config.VPN_SET_INTERVAL_TIME || 30000,
  VPN_RESTRICTED_COUNTRY: 'IN'
} as const;

interface CountryResponse {
  countryCode: string;
}

interface IpCheckAPIResponse {
  isVoiceAllowed: boolean;
  status: number
}

export class VpnConnectionStateHandler {
  private static instance: VpnConnectionStateHandler;
  private vpnCheckInterval: NodeJS.Timeout | null = null;
  private previousStatusCode: number = 0;
  private geoCountry: string = ''; // empty string is good instead of null, since it storing in sessionStorage. which again converts to string
  private isVpnConnected: boolean = false;
  private setNetworkModeCallback: ((mode: string) => void) | null = null;
  private setValidCountrCodeCallback: ((status: boolean) => void) | null = null;

  private constructor() {
    // Bind methods to ensure they maintain their context
    this.updateGeoCountry = this.updateGeoCountry.bind(this);
    this.getUserGeoCountry = this.getUserGeoCountry.bind(this);
    this.checkVpnStatus = this.checkVpnStatus.bind(this);

    this.initializeNetworkListener();
    this.updateGeoCountry();
    this.getVpnConnectionStatus();
  }

  /**
   * Initializes the VPN check context, setting up the necessary callbacks and starting the VPN check if applicable.
   * @param {Function} setNetworkMode - Callback to set the network mode (Online/Offline).
   * @param {Function} setIsValidCountryCode - Callback to set the validity of the country code.
   */
  public async initializeVpnCheckContext(setNetworkMode?: (mode: string) => void, setIsValidCountryCode?: (status: boolean) => void): Promise<void> {
    const { preferences: { pageHistoryPathname = '/' } = {} } = store.getState();
    const isVoiceAgent = sessionStorage.getItem('isVoiceAgent') === 'true';

    if (typeof setNetworkMode === 'function') {
      this.setNetworkModeCallback = setNetworkMode;
    }

    if (typeof setIsValidCountryCode === 'function') {
      this.setValidCountrCodeCallback = setIsValidCountryCode;
    }

    if (!isVoiceAgent || pageHistoryPathname === '/' || pageHistoryPathname === '/logout') {
      this.stopVpnCheck()
    } else {
      this.startVpnCheck();
    }
  }

  private initializeNetworkListener(): void {
    window.addEventListener('online', this.updateGeoCountry);
    window.addEventListener('offline', this.updateGeoCountry);
  }

  private removeNetworkListener(): void {
    window.removeEventListener('online', this.updateGeoCountry);
    window.removeEventListener('offline', this.updateGeoCountry);
  }

  private async updateGeoCountry(): Promise<void> {
    const { preferences: { pageHistoryPathname = '/' } = {} } = store.getState();
    const isVoiceAgent = sessionStorage.getItem('isVoiceAgent') === 'true';

    if (this.setNetworkModeCallback) {
      this.setNetworkModeCallback(navigator.onLine ? 'Online' : 'Offline');
    }

    if (!isVoiceAgent || pageHistoryPathname === '/' || pageHistoryPathname === '/logout') {
      console.log('VPN_DISCONNECT_CHECK - Skipping country code update for non-voice agents or on home/logout page');
      return;
    }

    console.log('VPN_DISCONNECT_CHECK - Network status changed, checking country code...');

    try {
      this.geoCountry = await this.getUserGeoCountry();
      this.setValidCountrCodeCallback(true);
      sessionStorage.setItem('VpnCountryCode', this.geoCountry);
      console.log(`VPN_DISCONNECT_CHECK - Geo Country Updated: ${this.geoCountry}`);
      this.checkVpnStatus(true); // retry the VPN immediately after updating the country code
    } catch (error) {
      console.error('VPN_DISCONNECT_CHECK - Country Code API Failed to Update Geo Country:', error);
      window.connect.getLog().debug('%s', `VPN_DISCONNECT_CHECK - Country Code API return undefined ${error}`)
      this.geoCountry = '';
      this.setValidCountrCodeCallback(false);
      sessionStorage.setItem('VpnCountryCode', '');
    }
  }

  public getVpnConnectionStatus(): boolean {
    return this.isVpnConnected;
  }

  public getGeoCountry(): string | null {
    return this.geoCountry;
  }

  public static getInstance(): VpnConnectionStateHandler {
    if (!VpnConnectionStateHandler.instance) {
      VpnConnectionStateHandler.instance = new VpnConnectionStateHandler();
    }
    return VpnConnectionStateHandler.instance;
  }

  private startVpnCheck(): void {
    this.vpnCheckInterval = setInterval(
      async () => {
        try {
          this.checkVpnStatus()
        } catch (error) {
          console.error('VPN_DISCONNECT_CHECK - Error validating IP:', error);
        }
      },
      Number(VPN_CONFIG.VPN_CHECK_INTERVAL_TIME)
    );
  }

  private setVpnStatus(statusCode: number): void {
    console.log(`VPN_DISCONNECT_CHECK - Code: ${statusCode}, previousStatusCode: ${this.previousStatusCode}`);

    const isVpnError = statusCode === VPN_CONFIG.ERROR_STATUS_CODE; // If is-voice-allowed api returns 550
    const statusChanged = this.previousStatusCode !== statusCode;
    const wasVpnError = this.previousStatusCode === VPN_CONFIG.ERROR_STATUS_CODE;

    if (isVpnError && statusChanged) {
      console.log(`VPN_DISCONNECT_CHECK - ${VPN_CONFIG.ERROR_STATUS_CODE} Condition Block Executed`);
      store.dispatch(changeVpnStatus(false)); // show the VPN disconnect popup
      this.isVpnConnected = true;
      ContactCenter.endCall();
    } else if ((!isVpnError && wasVpnError) || statusCode === 200) {
      console.log('VPN_DISCONNECT_CHECK - ENABLEMENT Condition Block Executed');
      store.dispatch(changeVpnStatus(true)); // hide the VPN disconnect popup
      this.isVpnConnected = false;
    }

    this.previousStatusCode = statusCode;
  }

  /**
   * This function fetches the user's geo country code from the cloudfront server.
   * It will called only when network change event is triggered.
   * @returns {Promise<string>} - Returns the country code of the user
   */
  public async getUserGeoCountry(): Promise<string> {
    const { contactCenter: { agentDetails: { ldap } = {} } = {} } = store.getState();
    const axiosInstance = createAxiosInstance(
      {
        retryDelay: VPN_CONFIG.RETRY_DELAY,
        maxRetries: VPN_CONFIG.MAX_RETRIES,
        timeout: VPN_CONFIG.DEFAULT_API_TIMEOUT,
        onMaxRetryTimesExceeded: (error: AxiosError, retryCount: number) => {
          console.error(`VPN_DISCONNECT_CHECK - Max retries (${retryCount}) exceeded for ${error.config?.url}`);
          // Is it good to show the VPN disconnect popup here? since Outside country should be able to access the app without VPN check
          this.setVpnStatus(VPN_CONFIG.ERROR_STATUS_CODE);
        }
      }
    );
    const response = await axiosInstance.get<CountryResponse>(`${config.VPN_COUNTRY_CF_DOMAIN}?agentid=${ldap}`);

    if (response.data?.countryCode === undefined) {
      // showing some extra text - Country Code Check Failed in  Popup
      console.log(`VPN_DISCONNECT_CHECK - Country Code API return undefined`)
      window.connect.getLog().debug('%s', `VPN_DISCONNECT_CHECK - Country Code API return undefined`)
      this.setValidCountrCodeCallback(false);
      throw new Error('Country code undefined');
    }

    return response.data.countryCode || ''
  }

  public async checkVpnStatus(showPopUp: boolean = true, canceledReqRetryCnt?: number): Promise<boolean> {
    const { preferences: { pageHistoryPathname = '/' } = {}, contactCenter: { agentDetails: { ldap } = {} } = {} } = store.getState();
    const url = `${config.VPN_DETECTION_SERVICE}/api/v1/agent/is-voice-allowed?agentid=${ldap}`;
    const maxRetries = (pageHistoryPathname !== '/' || pageHistoryPathname !== '/logout') ? VPN_CONFIG.MAX_RETRIES : 2;
    const countryCode = this.geoCountry || sessionStorage.getItem('VpnCountryCode') || await this.getUserGeoCountry();
    const isIndiaUser = countryCode === VPN_CONFIG.VPN_RESTRICTED_COUNTRY || false;

    if (!isIndiaUser) {
      this.stopVpnCheck();
      return;
    }

    // ?? preseved the falsy values except null or undefined. so it wont go infinite loop
    canceledReqRetryCnt = canceledReqRetryCnt ?? maxRetries;
    showPopUp = (pageHistoryPathname !== '/' || pageHistoryPathname !== '/logout') ? showPopUp : false;

    const axiosInstance = createAxiosInstance({
      retryDelay: VPN_CONFIG.RETRY_DELAY,
      maxRetries: maxRetries,
      timeout: Number(VPN_CONFIG.DEFAULT_API_TIMEOUT),
      customRetryCondition: true, // retry all error codes except 200. Server send 550 code if IP is not allowed.
      headers: {
        'x-api-key': 'oac-custom-panel',
        'Cache-Control': 'no-cache, no-store, must-revalidate', // prevent caching of responses - 304 Not Modified wont come
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      onMaxRetryTimesExceeded: (error, retryCount) => {
        console.error(`VPN_DISCONNECT_CHECK - Max retries (${retryCount}) exceeded for ${error.config?.url}`);
        // For now setting the error code to 550 for timeout as well to show VPN disconnect popup

        if (!error?.message?.includes('Network Error')) {
          showPopUp && this.setVpnStatus(VPN_CONFIG.ERROR_STATUS_CODE); // failed status code. don't show popup when network disconnet - CSME-96218
          throw new Error('MaxRetryTimesExceeded');
        }

        return;
      },
    });

    try {
      const response = await axiosInstance.get<IpCheckAPIResponse>(url);

      console.log('VPN_DISCONNECT_CHECK - VPN is Connected');
      this.setVpnStatus(response.status); // success status code - 200
      return true;
    } catch (error: any) {
      const retryCount = maxRetries - (canceledReqRetryCnt - 1);
      const delay = exponentialBackoff(retryCount, 1000);

      // for long pending requests cancelled after custom timeout
      if (error?.message === 'canceled' && canceledReqRetryCnt > 0) {
        // retry for canceled requests, since it cannot be handled via interceptors in axios-api-retry.ts.
        console.log(`VPN_DISCONNECT_CHECK - Retrying canceled request usecase. Attempts left: ${canceledReqRetryCnt - 1}`);
        await sleep(delay);
        return this.checkVpnStatus(showPopUp, canceledReqRetryCnt - 1);
      }

      if ((error.code === 'ENOTFOUND' || error?.message?.includes('ERR_NAME_NOT_RESOLVED')) && canceledReqRetryCnt > 0) {
        // This is a network-level error, so it's not thrown by axios directly
        console.log(`VPN_DISCONNECT_CHECK - Retrying ENOTFOUND | ERR_NAME_NOT_RESOLVED usecase. Attempts left: ${canceledReqRetryCnt - 1}`);
        await sleep(delay);
        return this.checkVpnStatus(showPopUp, canceledReqRetryCnt - 1);
      }

      console.log(`VPN_DISCONNECT_CHECK - Catch Error Block: ${error}`);
      window.connect.getLog().debug('%s', `VPN_DISCONNECT_CHECK - Catch Error Block ${error}`)

      !error?.message?.includes('Network Error') && showPopUp && this.setVpnStatus(VPN_CONFIG.ERROR_STATUS_CODE); // failed status code. don't show popup when network disconnet - CSME-96218
      return false;
    }
  }

  public clearVpnCheckContext(): void {
    this.previousStatusCode = 0;
    this.setNetworkModeCallback = null;
    this.setValidCountrCodeCallback = null;
    this.geoCountry = '';
    this.isVpnConnected = false;
    this.stopVpnCheck();
    this.removeNetworkListener();
  }

  public stopVpnCheck(): void {
    if (this.vpnCheckInterval) {
      clearInterval(this.vpnCheckInterval);
      this.vpnCheckInterval = null;
    }
  }
}

export const vpnConnectionStateHandler = VpnConnectionStateHandler.getInstance();
